import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { ArrowRight } from 'lucide-react';
import { WorkOrder, WorkOrderStatus } from '../../../types/workOrders';
import { ProjectModel } from '../../../types/projects';
import { formatDateForDisplay } from '../../../Helpers/DateUtils';
import DueDateInput, { isValidCalendarDate } from './DueDateInput';

interface ChangeProjectModalProps {
    show: boolean;
    onHide: () => void;
    workOrder: Pick<WorkOrder, 'title' | 'dueDate' | 'status'>;
    /** Title of the project the work order is linked to now; null = Inget projekt */
    fromProjectTitle: string | null;
    /** Project it is being moved to; null = unlink */
    toProject: ProjectModel | null;
    /**
     * `dueDate` is YYYY-MM-DD, null to clear, or undefined when the admin left
     * it as it was.
     */
    onConfirm: (options: { dueDate?: string | null }) => Promise<void>;
}

const projectPeriod = (project: ProjectModel): string | null => {
    if (!project.startDate) return null;
    const start = formatDateForDisplay(project.startDate);
    return project.plannedEndDate ? `${start} – ${formatDateForDisplay(project.plannedEndDate)}` : `från ${start}`;
};

/**
 * Confirms moving a work order (delmoment) to another project. The common case
 * is carrying unfinished delmoment over to next year's project, where the old
 * due date no longer makes sense — so the admin gets to confirm or change it
 * in the same step, and both are written atomically.
 */
const ChangeProjectModal: React.FC<ChangeProjectModalProps> = ({
    show,
    onHide,
    workOrder,
    fromProjectTitle,
    toProject,
    onConfirm
}) => {
    const [dueDate, setDueDate] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Only open work orders carry a due date (they live under workOrders/active)
    const canEditDueDate = workOrder.status === WorkOrderStatus.OPEN;
    const originalDueDate = workOrder.dueDate ? workOrder.dueDate.split(' ')[0] : '';

    useEffect(() => {
        if (show) {
            setDueDate(originalDueDate);
            setError(null);
        }
    }, [show, originalDueDate]);

    const period = toProject ? projectPeriod(toProject) : null;
    const outsideTargetPeriod = Boolean(
        toProject && isValidCalendarDate(dueDate) && (
            (toProject.startDate && dueDate < toProject.startDate) ||
            (toProject.plannedEndDate && dueDate > toProject.plannedEndDate)
        )
    );

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (canEditDueDate && dueDate && !isValidCalendarDate(dueDate)) {
            setError('Ogiltigt datum, använd formatet ÅÅÅÅ-MM-DD');
            return;
        }

        const dueDateChanged = canEditDueDate && dueDate !== originalDueDate;

        try {
            setSubmitting(true);
            setError(null);
            await onConfirm(dueDateChanged ? { dueDate: dueDate || null } : {});
            onHide();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Kunde inte ändra projekt');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!submitting) {
            setError(null);
            onHide();
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered dialogClassName="due-date-modal">
            <Modal.Header closeButton>
                <Modal.Title>Byt projekt</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
                            {error}
                        </Alert>
                    )}
                    <p className="text-muted mb-2">{workOrder.title}</p>
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <span>{fromProjectTitle ?? 'Inget projekt'}</span>
                        <ArrowRight size={16} className="text-muted flex-shrink-0" />
                        <span className="fw-semibold">{toProject?.title ?? 'Inget projekt'}</span>
                    </div>

                    {!fromProjectTitle && toProject && (
                        <Alert variant="warning" className="py-2 px-3 small">
                            Ärendet tas bort från Förvaltning-sidan och listas istället som
                            ett delmoment i projektet.
                        </Alert>
                    )}
                    {fromProjectTitle && !toProject && (
                        <Alert variant="info" className="py-2 px-3 small">
                            Ärendet visas på Förvaltning-sidan igen.
                        </Alert>
                    )}

                    {canEditDueDate && (
                        <Form.Group>
                            <Form.Label>Förfallodatum</Form.Label>
                            <DueDateInput value={dueDate} onChange={setDueDate} disabled={submitting} />
                            {period && (
                                <Form.Text className={outsideTargetPeriod ? 'text-warning' : 'text-muted'}>
                                    {outsideTargetPeriod
                                        ? `Förfallodatumet ligger utanför projektperioden ${period}.`
                                        : `Projektperiod: ${period}`}
                                </Form.Text>
                            )}
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                        Avbryt
                    </Button>
                    <Button variant="primary" type="submit" disabled={submitting}>
                        {submitting ? 'Sparar...' : 'Spara'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ChangeProjectModal;
